import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import axios from "axios";
import { Upload, Trash2, RefreshCw, FileText, Shield, Database, LogOut } from "lucide-react";
import StoneLogo from "@/components/StoneLogo";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "stoneia_admin_token";

const client = (token) => axios.create({
  baseURL: API,
  headers: { "X-Admin-Token": token },
});

const AdminLogin = ({ onAuth }) => {
  const [tok, setTok] = useState("");
  const submit = async () => {
    if (!tok.trim()) return;
    try {
      await client(tok).get("/admin/stats");
      localStorage.setItem(TOKEN_KEY, tok);
      onAuth(tok);
    } catch (e) {
      toast.error("Token inválido");
    }
  };
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#2C2C2C] px-4" data-testid="admin-login">
      <motion.div
        initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md p-8 rounded-2xl border border-[#555555] bg-[#3A3A3A]"
      >
        <div className="flex items-center gap-3 mb-7">
          <Shield className="text-[#C9A84C]" size={20} />
          <div className="text-[11px] tracking-[0.28em] uppercase text-[#C9A84C]">Painel Admin</div>
        </div>
        <h1 className="text-3xl font-light tracking-tight text-white mb-2" style={{ fontFamily: "Outfit" }}>
          StoneIA · Base Técnica
        </h1>
        <p className="text-zinc-400 text-[13px] mb-8 leading-relaxed">
          Acesso restrito. Insira o token administrativo para gerenciar a base de conhecimento.
        </p>
        <input
          type="password"
          value={tok}
          onChange={(e) => setTok(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && submit()}
          placeholder="X-Admin-Token"
          autoFocus
          data-testid="admin-token-input"
          className="w-full bg-[#2A2A2A] border border-[#C9A84C]/40 focus:border-[#C9A84C] outline-none rounded-xl px-4 py-3 text-[14px] text-white placeholder:text-zinc-500 transition mb-4"
        />
        <Button
          onClick={submit}
          data-testid="admin-login-btn"
          className="w-full !bg-[#C9A84C] !text-black hover:!bg-[#D9BC6A] h-11 rounded-full font-medium"
        >
          Entrar
        </Button>
        <div className="mt-6 text-center">
          <Link to="/" className="text-zinc-500 text-[12px] hover:text-white">← Voltar à StoneIA</Link>
        </div>
      </motion.div>
    </div>
  );
};

const StatCard = ({ label, value, testid }) => (
  <div className="p-5 rounded-2xl border border-[#555555] bg-[#3A3A3A]" data-testid={testid}>
    <div className="text-[10.5px] uppercase tracking-[0.2em] text-zinc-400">{label}</div>
    <div className="text-3xl font-light text-white mt-2" style={{ fontFamily: "Outfit" }}>{value}</div>
  </div>
);

const AdminDashboard = ({ token, onLogout }) => {
  const [stats, setStats] = useState(null);
  const [docs, setDocs] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [busyDoc, setBusyDoc] = useState(null);
  const api = client(token);

  const refresh = async () => {
    try {
      const [s, d] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/documents"),
      ]);
      setStats(s.data);
      setDocs(d.data.documents || []);
    } catch (e) {
      if (e.response?.status === 401) {
        toast.error("Sessão expirou");
        onLogout();
      } else {
        toast.error("Falha ao carregar dados");
      }
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { refresh(); }, []);

  const handleUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.name.toLowerCase().endsWith(".pdf")) {
      toast.error("Apenas PDFs");
      return;
    }
    const fd = new FormData();
    fd.append("file", f);
    setUploading(true);
    try {
      const r = await api.post("/admin/documents/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (r.data.status === "duplicate") {
        toast.info(`Já indexado: ${r.data.filename}`);
      } else {
        toast.success(`${r.data.chunks} chunks indexados`);
      }
      await refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Falha no upload");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const handleDelete = async (doc) => {
    if (!confirm(`Excluir "${doc.filename}"?\nIsto remove TODOS os vetores associados do Pinecone.`)) return;
    setBusyDoc(doc.id);
    try {
      await api.delete(`/admin/documents/${doc.id}`);
      toast.success("Documento removido");
      await refresh();
    } catch (e) {
      toast.error("Falha ao excluir");
    } finally {
      setBusyDoc(null);
    }
  };

  const handleReindex = async (doc) => {
    setBusyDoc(doc.id);
    try {
      const r = await api.post(`/admin/documents/${doc.id}/reindex`);
      toast.success(`Reindexado: ${r.data.vectors_upserted} vetores`);
      await refresh();
    } catch (e) {
      toast.error("Falha ao reindexar");
    } finally {
      setBusyDoc(null);
    }
  };

  return (
    <div className="min-h-screen bg-[#2C2C2C] text-white" data-testid="admin-dashboard">
      <header className="border-b border-[#555555] bg-[#3A3A3A]/95 backdrop-blur-xl sticky top-0 z-20">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/"><StoneLogo size={40} wordmarkSize={22} /></Link>
            <div className="text-[10.5px] tracking-[0.22em] uppercase text-[#C9A84C] hidden md:block">
              Base Técnica
            </div>
          </div>
          <button
            onClick={onLogout}
            data-testid="admin-logout"
            className="flex items-center gap-2 text-[12.5px] text-zinc-300 hover:text-white border border-[#555555] hover:border-[#C9A84C]/60 rounded-full px-4 py-1.5 transition"
          >
            <LogOut size={13} /> Sair
          </button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 lg:px-10 py-10">
        <div className="flex items-end justify-between mb-8">
          <div>
            <p className="text-[10.5px] tracking-[0.28em] uppercase text-zinc-500 mb-3">Conhecimento mineral</p>
            <h1 className="text-4xl font-light tracking-tight" style={{ fontFamily: "Outfit" }}>
              Documentos indexados
            </h1>
          </div>
          <label
            className={`flex items-center gap-2 px-5 h-11 rounded-full font-medium cursor-pointer transition ${
              uploading ? "opacity-50 cursor-wait" : ""
            }`}
            style={{ backgroundColor: "#C9A84C", color: "#0A0A0A" }}
            data-testid="upload-label"
          >
            <Upload size={15} />
            {uploading ? "Indexando…" : "Upload PDF"}
            <input
              type="file"
              accept="application/pdf,.pdf"
              hidden
              onChange={handleUpload}
              disabled={uploading}
              data-testid="admin-upload-input"
            />
          </label>
        </div>

        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
            <StatCard label="Documentos" value={stats.documents ?? 0} testid="stat-docs" />
            <StatCard label="Chunks" value={stats.chunks ?? 0} testid="stat-chunks" />
            <StatCard label="Vetores Pinecone" value={stats.pinecone?.total_vectors ?? 0} testid="stat-vectors" />
            <StatCard label="Modelo" value={stats.model || "—"} testid="stat-model" />
          </div>
        )}

        {docs.length === 0 ? (
          <div
            className="text-center py-20 rounded-2xl border border-dashed border-[#555555] bg-[#3A3A3A]/40"
            data-testid="admin-empty"
          >
            <Database size={28} className="mx-auto text-[#C9A84C] mb-4" />
            <p className="text-zinc-300 text-[15px] mb-1">Nenhum documento ainda</p>
            <p className="text-zinc-500 text-[13px]">Faça upload do primeiro PDF técnico para alimentar a StoneIA.</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="admin-doc-list">
            {docs.map((d) => (
              <div
                key={d.id}
                className="flex items-center gap-4 p-5 rounded-2xl border border-[#555555] bg-[#3A3A3A] hover:border-[#C9A84C]/40 transition"
                data-testid={`admin-doc-${d.id}`}
              >
                <div className="w-11 h-11 rounded-xl bg-[#2A2A2A] border border-[#555555] flex items-center justify-center shrink-0">
                  <FileText size={18} className="text-[#C9A84C]" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[15px] text-white truncate">{d.filename}</div>
                  <div className="text-[11.5px] text-zinc-400 mt-0.5">
                    {d.pages} páginas · {d.chunks} chunks · {(d.size_bytes / 1024).toFixed(1)} KB ·{" "}
                    {new Date(d.created_at).toLocaleString("pt-BR")}
                  </div>
                </div>
                <button
                  onClick={() => handleReindex(d)}
                  disabled={busyDoc === d.id}
                  data-testid={`admin-reindex-${d.id}`}
                  className="flex items-center gap-1.5 text-[12.5px] text-zinc-300 hover:text-[#C9A84C] border border-[#555555] hover:border-[#C9A84C]/50 rounded-full px-3 py-1.5 transition disabled:opacity-40"
                  title="Reprocessar embeddings"
                >
                  <RefreshCw size={13} className={busyDoc === d.id ? "animate-spin" : ""} />
                  Reindexar
                </button>
                <button
                  onClick={() => handleDelete(d)}
                  disabled={busyDoc === d.id}
                  data-testid={`admin-delete-${d.id}`}
                  className="flex items-center gap-1.5 text-[12.5px] text-zinc-400 hover:text-red-400 border border-[#555555] hover:border-red-400/40 rounded-full px-3 py-1.5 transition disabled:opacity-40"
                >
                  <Trash2 size={13} />
                  Excluir
                </button>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

const AdminPage = () => {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const handleLogout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
  };
  if (!token) return <AdminLogin onAuth={setToken} />;
  return <AdminDashboard token={token} onLogout={handleLogout} />;
};

export default AdminPage;
